import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import type { BankFeedSummaryAccount } from 'domain/bankFeed/schemas';
import { useBankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from 'pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import SelectedAccountChip from 'pages/company/BankFeedRoute/components/FilterToolbar/shared/SelectedAccountChip';

const CARD_SELECT_WIDTH = 280;
// The dropdown popup is wider than the (compact) input so long card labels fit — mirrors the
// Bank filter's popup width.
const CARD_POPUP_WIDTH = 420;
const AVATAR_SX = { width: 24, height: 24, flexShrink: 0 } as const;
const ellipsisSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

// Credit Card tab account filter (DA-19483): a single-value MUI Autocomplete — exactly one
// card is always selected and the grid shows only that card's transactions. Unlike the Bank
// tab's multi-select (BankAccountsFilter) there is no "All" / Select-All / chips state; the
// wire contract stays toolbarFilters.accountIds but always carries exactly one id on CARD.
const CardAccountFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary, isSummaryLoading } = useBankFeedRouteContext();

  const { accountIds } = toolbarFilters;

  // Track open state so the closed trigger shows the selected card as a chip (collapsing the text
  // input) while the open state exposes the search input — mirroring the Bank filter.
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () => summary?.items.find((i) => i.accountType === accountType)?.accounts ?? [],
    [summary, accountType],
  );

  // The single selected card. accountIds carries exactly one id on CARD (seeded to the first
  // card by BankFeedRoute); fall back to the first option for display while the seed settles.
  const selectedCard = useMemo(
    () => options.find((o) => o.id === accountIds[0]) ?? options[0] ?? null,
    [options, accountIds],
  );

  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;
  const isReady = !isSummaryLoading && !isEmptyView && options.length > 0;

  const label = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.accounts.labelCard',
    defaultMessage: 'Credit card',
  });

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, value: BankFeedSummaryAccount) => {
      setToolbarFilters((prev) => (prev.accountIds[0] === value.id ? prev : { ...prev, accountIds: [value.id] }));
    },
    [setToolbarFilters],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: BankFeedSummaryAccount) => {
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          <Avatar src={option.picture ?? undefined} alt={option.label} sx={{ ...AVATAR_SX, mr: 1 }} />
          <Tooltip title={option.label} placement="right">
            <Box component="span" sx={ellipsisSx}>
              {option.label}
            </Box>
          </Tooltip>
        </li>
      );
    },
    [],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={label}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {isSummaryLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    ),
    [label, isSummaryLoading],
  );

  // Render the selected card as a chip (matching the Bank filter) instead of the default input
  // text, so both tabs look identical; null while open so the search input shows for typing.
  const renderValue = useCallback(
    (value: BankFeedSummaryAccount) => (open ? null : <SelectedAccountChip label={value.label} />),
    [open],
  );

  // Disabled placeholder while summary loads / in a pre-data empty state / no cards: a single
  // Autocomplete needs a non-null value under disableClearable, so render a disabled field
  // until a card is available (mirrors the Bank filter's disabled state).
  if (!isReady || selectedCard === null) {
    return (
      <TextField
        label={label}
        size="small"
        disabled
        sx={{ width: CARD_SELECT_WIDTH, backgroundColor: 'common.white' }}
        slotProps={{
          input: {
            endAdornment: isSummaryLoading ? <CircularProgress color="inherit" size={16} /> : undefined,
          },
        }}
      />
    );
  }

  return (
    <Autocomplete<BankFeedSummaryAccount, false, true, false>
      disableClearable
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      size="small"
      // Mirror the Bank filter: keep the value on one line, inset it like the sibling Selects, and
      // collapse the empty text input when closed so the selected-card chip claims the row.
      sx={{
        width: CARD_SELECT_WIDTH,
        backgroundColor: 'common.white',
        '& .MuiInputBase-root': { flexWrap: 'nowrap' },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { pl: 1.75 },
        '& .MuiAutocomplete-input:first-of-type': open ? {} : { width: 0, minWidth: 0, p: 0 },
      }}
      options={options}
      value={selectedCard}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      // bottom-start pins the (wider) popup to the input's bottom-left edge; width matches the
      // Bank filter's popup so long card labels fit.
      slotProps={{ popper: { placement: 'bottom-start', style: { width: CARD_POPUP_WIDTH } } }}
      renderValue={renderValue}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );
};

export default CardAccountFilter;
